import { createSelector } from 'reselect';

import { DEFAULT_CURRENCY_CODE } from '@/constants/settings';
import {
  ADDONS_ADDED_TO_INVOICE_BY_DEFAULT,
  SERVICE_CHARGE_ADD_ON_CODE,
  VOYAGE_PROTECTION_ADD_ON_CODE,
} from '@/ducks/addOns/constants';
import { getAddOnPriceFromCalcInvoiceData } from '@/ducks/addOns/utils';
import {
  selectCabinInvoices,
  selectCalculateInvoiceIsLoading,
  selectIsPayInFullPromoApplied,
  selectIsPayInFullPromoAvailable,
  selectPaymentSchedule,
  selectPriceBreakdown,
} from '@/ducks/pages/summary/selectors';
import { selectIsVoyageProtectionAdded } from '@/ducks/travelParty/selectors';
import { getGrandTotal } from '@/helpers/data/mappers/Summary';
import roundUpTwoDecimals from '@/helpers/money/roundUpTwoDecimals';
import { DiscountType, type TAddOnPrice, type TBookingPriceDetail } from '@/infra/types/cabin';

const defaultAddOnChecker = () => false;

export const selectCalcMainValues = createSelector(
  [
    selectCabinInvoices,
    selectCalculateInvoiceIsLoading,
    selectPaymentSchedule,
    selectIsPayInFullPromoApplied,
    selectIsPayInFullPromoAvailable,
    selectPriceBreakdown,
    selectIsVoyageProtectionAdded,
    (
      _,
      {
        isAddOnAddedByCode,
        isAddOnsLoaded,
        isMNVV,
        isPayInFullPromoAppliedWhenAvailable,
      }: {
        isAddOnAddedByCode?: (code: string) => boolean;
        isAddOnsLoaded?: boolean;
        isMNVV?: boolean;
        isPayInFullPromoAppliedWhenAvailable?: boolean;
      } = {},
    ) => ({
      isAddOnAddedByCode: isAddOnAddedByCode || defaultAddOnChecker,
      isAddOnsLoaded: isAddOnsLoaded || false,
      isMNVV: isMNVV || false,
      isPayInFullPromoAppliedWhenAvailable,
    }),
  ],
  (
    cabinInvoices,
    invoiceIsLoading,
    paymentSchedule,
    _isPayInFullPromoApplied,
    isPayInFullPromoAvailable,
    invoiceData,
    isVoyageProtectionAdded,
    {
      isAddOnAddedByCode,
      isAddOnsLoaded,
      isMNVV,
      isPayInFullPromoAppliedWhenAvailable, // handle the case when user is on payment+ page (temporary)
    },
  ) => {
    const isLoading = invoiceIsLoading || !isAddOnsLoaded;

    const isPayInFullPromoApplied =
      isPayInFullPromoAppliedWhenAvailable && isPayInFullPromoAvailable ? true : _isPayInFullPromoApplied;

    const {
      cabins,
      currencyCode = DEFAULT_CURRENCY_CODE,
      deposit,
      discountList: initialDiscountList,
      paidAmount,
      sailorsAmount,
    } = invoiceData || {};
    const cabin = cabins?.[0];
    const cabinInvoice = cabinInvoices?.[0];
    const bookingPriceDetails = cabinInvoice?.bookingPriceDetails;
    const fullPayPrice = cabinInvoice?.fullPayPrice;

    const fullPayGrandTotalAmount = getGrandTotal(cabinInvoice, 'fullPayPrice');
    const bookingPriceGrandTotalAmount = getGrandTotal(cabinInvoice, 'bookingPriceDetails');

    const firstPaymentZero = paymentSchedule?.[0]?.depositAmount?.amount === 0;
    const isDuePaymentAvailable = !isPayInFullPromoApplied && (deposit?.value !== 0 || (isMNVV && firstPaymentZero));

    const taxesAndFeesAmount = cabin?.items?.taxes?.value;
    const govtFeesAmount = cabin?.items?.govtFees?.value;
    const portFeesAmount = cabin?.items?.portFees?.value;

    const gratuityPerGuestPerNight = cabinInvoice?.bookingPriceDetails?.gratuityPerGuestPerNight ?? 0;
    const gratuitySavingsAmount = cabinInvoice?.bookingPriceDetails?.gratuitySavingsAmount ?? 0;
    const originalGratuityAmount = cabinInvoice?.bookingPriceDetails?.originalGratuityAmount ?? 0;

    const discountList = initialDiscountList?.filter((discount) => discount.priceTypeText !== DiscountType.PNSV);

    const priceDetails = isPayInFullPromoApplied ? fullPayPrice : bookingPriceDetails;
    const insuranceAmount =
      priceDetails?.addOnPrices?.find(({ code }) => code === VOYAGE_PROTECTION_ADD_ON_CODE)?.amount || 0;
    const gratuityTotalAmount =
      priceDetails?.addOnPrices?.find(({ code }) => code === SERVICE_CHARGE_ADD_ON_CODE)?.amount || 0;

    const totalAmountAdjuster = (priceDetails?: Partial<TBookingPriceDetail>) =>
      priceDetails?.addOnPrices?.reduce(
        (value: number, { amount, code }: TAddOnPrice) =>
          ADDONS_ADDED_TO_INVOICE_BY_DEFAULT.includes(code) && !isAddOnAddedByCode(code) ? value + amount : value,
        0,
      ) || 0;

    const depositAmount = paymentSchedule?.[0]?.depositAmount?.amount || 0;

    return {
      addOnsPrice: getAddOnPriceFromCalcInvoiceData({
        cabinInvoice,
        payInFullPromoApplied: isPayInFullPromoApplied,
      }),
      bookingPriceGrandTotalAmount,
      cabin,
      cabinInvoice,
      cabinPrice: roundUpTwoDecimals(
        (bookingPriceDetails?.amount || 0) -
          (bookingPriceDetails?.addOnPrices?.reduce((total, { amount }) => total + amount, 0) || 0),
      ),
      currencyCode,
      deposit,
      depositAmount,
      depositAmountWithAddOns:
        depositAmount +
        getAddOnPriceFromCalcInvoiceData({
          cabinInvoice,
          payInFullPromoApplied: false,
        }),
      discountList,
      fullPayGrandTotalAmount,
      govtFeesAmount,
      grandTotalAmount: roundUpTwoDecimals((priceDetails?.amount || 0) - totalAmountAdjuster(priceDetails)),
      gratuityAmount: isAddOnAddedByCode(SERVICE_CHARGE_ADD_ON_CODE) ? gratuityTotalAmount : 0,
      gratuityPerGuestPerNight,
      gratuitySavingsAmount,
      gratuityTotalAmount,
      insuranceAmount,
      isDuePaymentAvailable,
      isLoading,
      isMNVV,
      isPayInFullPromoApplied,
      isPayInFullPromoAvailable,
      isVoyageProtectionAdded,
      originalGrandTotalAmount: roundUpTwoDecimals(
        (bookingPriceDetails?.amount || 0) - totalAmountAdjuster(bookingPriceDetails),
      ),
      originalGratuityAmount,
      originalTotalAmount: roundUpTwoDecimals(
        (bookingPriceDetails?.originalAmount || 0) - totalAmountAdjuster(bookingPriceDetails),
      ),
      paidAmount,
      payInFullGrandTotalAmount: roundUpTwoDecimals((fullPayPrice?.amount || 0) - totalAmountAdjuster(fullPayPrice)),
      portFeesAmount,
      sailorsAmount,
      taxesAndFeesAmount,
      tripInsurance: isAddOnAddedByCode(VOYAGE_PROTECTION_ADD_ON_CODE) ? insuranceAmount : 0,
    };
  },
);
